import { Component, OnDestroy, OnInit, TemplateRef } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { ISession } from '../../../models/session';
import { TranslateService } from '@ngx-translate/core';
import { SessionsService } from '../sessions.service';
import { first, map, tap, flatMap, switchMap, takeUntil } from 'rxjs/operators';
import { BsModalService } from 'ngx-bootstrap/modal';
import { BsModalRef } from 'ngx-bootstrap/modal/bs-modal-ref.service';
import { PluginsService } from '../../plugins/plugins.service';
import { PreviewService } from '../../preview/preview.service';
import { ILink } from '../../../models/link';
import { AuthService } from '../../auth/auth.service';
import { interval } from 'rxjs/observable/interval';
import { IDownloadStatus, VideoService } from '../video.service';
import { Subject } from 'rxjs/Subject';
import { TagsService } from '../tags.service';
import { forkJoin } from 'rxjs/observable/forkJoin';
import { prop, head } from 'ramda/es';
import { ConfirmationModalComponent } from '../../modals/confirmation/confirmation-modal.component';
import { never } from 'rxjs/observable/never';

const REPLAY_VIDEO_IFRAME_TAG = 'webevent.replay.video.iframe';
const REPLAY_VIDEP_IFRAME_DEDAULT_CODE =
  '<iframe src="(!--ct:webevent.replay.url--)?title=0&byline=0&portrait=0" width="100%" height="520" frameborder="0" webkitallowfullscreen mozallowfullscreen allowfullscreen></iframe>';

const REPLAY_VIDEO_CUSTOM_IFRAME_TAG = 'webevent.replay.iframecode';
const REPLAY_VIDEO_MODE_TAG = 'webevent.replay.mode';

@Component({
  templateUrl: './replay.component.html',
  styleUrls: ['./replay.component.scss']
})
export class ReplayPublishComponent implements OnInit, OnDestroy {
  public session: ISession;
  public publishLoading: Boolean;
  public replay: Boolean;
  public replayAllowed: Boolean;
  public alreadyPublished: Boolean;

  public modalRef: BsModalRef;
  public link: ILink;

  private pullVideoStats$;
  private pullVideoStatsStop$ = new Subject();
  public downloadReplayUrl: string;
  public downloadRecordingUrl: string;

  public replayIframe: any;
  public replayMode: 'default' | 'iframe' = 'iframe';

  constructor(
    private route: ActivatedRoute,
    private translateService: TranslateService,
    private sessionsService: SessionsService,
    private authService: AuthService,
    private modalService: BsModalService,
    private pluginsService: PluginsService,
    private previewService: PreviewService,
    private router: Router,
    private videoService: VideoService,
    private tagService: TagsService
  ) {}

  ngOnInit() {
    const parentData$ = this.route.parent.data;
    parentData$
      .pipe(
        first(),
        map(route => route.data),
        tap((session: ISession) => {
          this.session = session;
          this.checkAllowance();
        })
      )
      .subscribe();

    this.pluginsService.getAll(this.session).subscribe(plugins => {
      this.link = this.previewService
        .getLinks('webevent', this.session, plugins)
        .find(link => link.name.indexOf('replay') >= 0);
      this.replay = plugins.find(
        x => (x.name === 'transcript_publish' || x.name === 'video_replay_publish') && x.isActivated
      );
    });

    this.resolveDownloadUrls();

    const getTagValue = name => {
      return this.tagService.getTag(this.session, name).map(tags => {
        return prop('value', head(tags));
      });
    };

    const getReplayIframeCode$ = getTagValue(REPLAY_VIDEO_CUSTOM_IFRAME_TAG);
    const getReplayMode$ = getTagValue(REPLAY_VIDEO_MODE_TAG);

    forkJoin(getReplayIframeCode$, getReplayMode$).subscribe(
      ([iframe, mode]: [string, 'default' | 'iframe']) => {
        this.replayIframe = iframe;
        this.replayMode = mode || 'iframe';
      }
    );
  }

  public ngOnDestroy() {
    if (this.pullVideoStats$) {
      this.pullVideoStats$.unsubscribe();
    }
    this.pullVideoStatsStop$.unsubscribe();
  }

  checkAllowance() {
    this.replayAllowed =
      this.session.eurostudioStatus === 'STOPPED' || this.session.eurostudioStatus === 'CLOSED';
    this.alreadyPublished = this.session.eurostudioStatus === 'PUBLISHED';
  }

  openModal(template: TemplateRef<any>) {
    this.modalRef = this.modalService.show(template, { class: 'inmodal' });
  }

  closeModal() {
    this.modalRef.hide();
  }

  publish() {
    this.publishLoading = true;
    this.closeModal();
    this.sessionsService
      .publish(this.session)
      .subscribe(result => {
        let resultJson = { message: '' };
        if (result && result._body) {
          try {
            resultJson = JSON.parse(result._body);
          } catch (err) {}
        }
        if (resultJson && resultJson.message) {
          toastr.warning(resultJson.message);
        } else {
          toastr.success(this.translateService.instant('action.success'));
        }
        this.sessionsService.setComplete(this.session, 'replay');
      })
      .add(() => {
        this.sessionsService
          .getOne(this.session.id)
          .subscribe(session => {
            this.session = session;
            this.checkAllowance();
          })
          .add(() => {
            this.publishLoading = false;
          });
      });
  }

  resetDownloadUrls() {
    this.downloadReplayUrl = null;
    this.downloadRecordingUrl = null;
  }

  extractDownloadUrls(status: IDownloadStatus): boolean {
    if (status) {
      this.downloadReplayUrl =
        status.vimeoScreencastDownloadUrl || status.liveeScreencastDownloadUrl;
      this.downloadRecordingUrl =
        status.vimeoRecordingDownloadUrl || status.liveeRecordingDownloadUrl;
      console.log('downloadReplayUrl', this.downloadReplayUrl);
      console.log('downloadRecordingUrl', this.downloadRecordingUrl);
      console.log('vimeoScreencastStatus', status.vimeoScreencastStatus);
      console.log('vimeoRecordingStatus', status.vimeoRecordingStatus);
      if (
        (status.vimeoScreencastStatus && status.vimeoScreencastStatus === 'transcoding') ||
        (status.vimeoRecordingStatus && status.vimeoRecordingStatus === 'transcoding')
      ) {
        // wait for download link
        return false;
      }
    }
    return true;
  }

  downloadReplay() {
    if (this.downloadReplayUrl) {
      window.location.href = this.downloadReplayUrl;
    }
  }

  downloadRecording() {
    if (this.downloadRecordingUrl) {
      window.location.href = this.downloadRecordingUrl;
    }
  }

  resolveDownloadUrls() {
    this.resetDownloadUrls();
    this.videoService.getDownloadStatus(this.session.id).subscribe((status: IDownloadStatus) => {
      if (!this.extractDownloadUrls(status)) {
        if (this.pullVideoStats$) {
          this.pullVideoStats$.unsubscribe();
        }
        this.pullVideoStats$ = interval(5000)
          .pipe(
            switchMap(() => this.videoService.getDownloadStatus(this.session.id)),
            takeUntil(this.pullVideoStatsStop$)
          )
          .subscribe((downloadStatus: IDownloadStatus) => {
            if (this.extractDownloadUrls(downloadStatus)) {
              this.pullVideoStatsStop$.next(true);
            }
          });
      }
    });
  }

  private updateReplayTag(name, value) {
    const tag = {
      name,
      value: value || '',
      description: 'created by EVA',
      type: 'text'
    };
    return this.tagService.updateTag(this.session, tag);
  }

  saveReplay() {
    const modal = this.modalService.show(ConfirmationModalComponent);
    (<ConfirmationModalComponent>modal.content).showConfirmationModal(
      this.translateService.instant('sessions.publish.own_video.confirm')
    );
    const closeSource$ = (<ConfirmationModalComponent>modal.content).onClose;
    const save = result => {
      if (result !== true) return never();
      const customActions = [
        this.updateReplayTag(REPLAY_VIDEO_CUSTOM_IFRAME_TAG, this.replayIframe),
        this.updateReplayTag(REPLAY_VIDEO_MODE_TAG, this.replayMode)
      ];
      if (this.replayMode === 'iframe') {
        return forkJoin([
          ...customActions,
          this.updateReplayTag(REPLAY_VIDEO_IFRAME_TAG, this.replayIframe)
        ]);
      } else {
        return forkJoin([
          ...customActions,
          this.updateReplayTag(REPLAY_VIDEO_IFRAME_TAG, REPLAY_VIDEP_IFRAME_DEDAULT_CODE)
        ]);
      }
    };
    closeSource$.pipe(flatMap(save)).subscribe(
      () => toastr.success(this.translateService.instant('action.success')),
      () => toastr.error(this.translateService.instant('action.failed'))
    );
  }
}



// WEBPACK FOOTER //
// ./src/app/modules/sessions/replay/replay.component.ts